import { useEffect, useState } from "react";
import { User, ShieldCheck, Truck, Save, KeyRound, ArrowUpCircle, MapPin, Plus, Pencil, Trash2, Star } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { toast } from "sonner";
import API from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

function formatRole(role) {
  if (role === "retail") return "Retail";
  if (role === "reseller") return "Reseller";
  if (role === "agen") return "Agen";
  if (role === "mitra" || role === "produsen") return "Mitra";
  if (role === "distributor") return "Distributor";
  if (role === "admin") return "Admin";
  return role || "Retail";
}

export default function ProfilePage() {
  const { user, checkAuth } = useAuth();
  const isAdmin = user?.role === "admin";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [applyingMembership, setApplyingMembership] = useState(false);
  const [membershipOpen, setMembershipOpen] = useState(false);
  const [membershipRole, setMembershipRole] = useState("member");
  const [form, setForm] = useState({
    name: "",
    email: "",
    role: "",
    phone: "",
    address: "",
    postal_code: "",
    default_sender_name: "",
    default_sender_phone: "",
    membership_requested_role: "",
    membership_application_status: "",
  });
  const [passwordForm, setPasswordForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const blankAddressForm = { label: "", recipient_name: "", recipient_phone: "", address: "", postal_code: "", sender_type: "self", sender_name: "", sender_phone: "", sender_address: "", is_default: false };
  const [addresses, setAddresses] = useState([]);
  const [addressForm, setAddressForm] = useState(blankAddressForm);
  const [editingAddressId, setEditingAddressId] = useState(null);
  const [savingAddress, setSavingAddress] = useState(false);

  const loadAddresses = async () => {
    try {
      const res = await API.get("/address-book");
      setAddresses(res.data.addresses || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal memuat buku alamat");
    }
  };

  const loadProfile = async () => {
    setLoading(true);
    try {
      const res = await API.get("/auth/me");
      const data = res.data || {};
      setForm({
        name: data.name || "",
        email: data.email || "",
        role: data.role || "",
        phone: data.phone || "",
        address: data.address || "",
        postal_code: data.postal_code || "",
        default_sender_name: data.default_sender_name || "",
        default_sender_phone: data.default_sender_phone || "",
        membership_requested_role: data.membership_requested_role || "",
        membership_application_status: data.membership_application_status || "",
      });
      await loadAddresses();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal memuat profil");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadProfile(); }, []);

  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Nama wajib diisi");
      return;
    }
    setSaving(true);
    try {
      const res = await API.put("/auth/profile", {
        name: form.name,
        phone: form.phone,
        address: form.address,
        postal_code: form.postal_code,
        default_sender_name: form.default_sender_name,
        default_sender_phone: form.default_sender_phone,
      });
      setForm((prev) => ({ ...prev, ...(res.data || {}) }));
      await checkAuth();
      toast.success("Profil berhasil disimpan");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menyimpan profil");
    } finally {
      setSaving(false);
    }
  };


  const resetAddressForm = () => {
    setAddressForm(blankAddressForm);
    setEditingAddressId(null);
  };

  const editAddress = (address) => {
    setEditingAddressId(address.id);
    setAddressForm({
      label: address.label || "",
      recipient_name: address.recipient_name || "",
      recipient_phone: address.recipient_phone || "",
      address: address.address || "",
      postal_code: address.postal_code || "",
      sender_type: address.sender_type || "self",
      sender_name: address.sender_name || "",
      sender_phone: address.sender_phone || "",
      sender_address: address.sender_address || "",
      is_default: !!address.is_default,
    });
  };

  const handleSaveAddress = async (e) => {
    e.preventDefault();
    if (!addressForm.recipient_name.trim() || !addressForm.recipient_phone.trim() || !addressForm.address.trim()) {
      toast.error("Nama penerima, nomor HP, dan alamat wajib diisi");
      return;
    }
    setSavingAddress(true);
    try {
      const payload = { ...addressForm, label: addressForm.label || "Alamat" };
      if (editingAddressId) await API.put(`/address-book/${editingAddressId}`, payload);
      else await API.post("/address-book", payload);
      toast.success(editingAddressId ? "Alamat berhasil diperbarui" : "Alamat berhasil ditambahkan");
      resetAddressForm();
      await loadAddresses();
      await loadProfile();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menyimpan alamat");
    } finally {
      setSavingAddress(false);
    }
  };

  const setDefaultAddress = async (addressId) => {
    try {
      await API.put(`/address-book/${addressId}/default`);
      toast.success("Alamat default diperbarui");
      await loadAddresses();
      await loadProfile();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengubah alamat default");
    }
  };

  const deleteAddress = async (addressId) => {
    if (!window.confirm("Arsipkan alamat ini?")) return;
    try {
      await API.delete(`/address-book/${addressId}`);
      toast.success("Alamat diarsipkan");
      await loadAddresses();
      if (editingAddressId === addressId) resetAddressForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengarsipkan alamat");
    }
  };

  const handleApplyMembership = async () => {
    const phone = (form.phone || "").trim();
    if (!phone) {
      toast.error("Nomor WhatsApp wajib diisi sebelum mengajukan membership");
      return;
    }
    setApplyingMembership(true);
    try {
      const res = await API.post("/auth/membership-application", {
        requested_role: membershipRole,
        phone,
      });
      setForm((prev) => ({ ...prev, ...(res.data || {}) }));
      await checkAuth();
      toast.success("Pengajuan membership dikirim dan menunggu approval admin");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengirim pengajuan membership");
    } finally {
      setApplyingMembership(false);
    }
  };

  const handleSavePassword = async (e) => {
    e.preventDefault();
    if (!passwordForm.current_password || !passwordForm.new_password) {
      toast.error("Lengkapi password lama dan password baru");
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error("Password baru minimal 6 karakter");
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error("Konfirmasi password tidak sama");
      return;
    }
    setSavingPassword(true);
    try {
      await API.put("/auth/password", {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });
      setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
      toast.success("Password berhasil diganti");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengganti password");
    } finally {
      setSavingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF9F6]" data-testid="profile-page">
        <Header />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="h-64 bg-stone-100 animate-pulse rounded-2xl" />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="profile-page">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6">
      <div>
        <p className="text-xs uppercase tracking-[0.22em] text-stone-400 font-semibold">Profil Saya</p>
        <h1 className="font-serif text-3xl text-stone-900 mt-1">Akun Saya</h1>
        <p className="text-sm text-stone-500 mt-2">Lihat data akun, atur data pengirim default, dan kelola keamanan akun.</p>
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl shadow-sm p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4 min-w-0">
          <div className="w-14 h-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl font-serif shrink-0">
            {(form.name || user?.name || "U").slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0">
            <h2 className="font-serif text-xl text-stone-900 truncate">{form.name || "-"}</h2>
            <p className="text-sm text-stone-500 truncate">{form.email}</p>
            <div className="mt-2 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider bg-stone-100 text-stone-700 px-3 py-1.5 rounded-full">
              <ShieldCheck className="w-3.5 h-3.5" /> {formatRole(form.role)}
            </div>
          </div>
        </div>
        <div className="w-full sm:w-auto sm:min-w-[320px] bg-stone-50 border border-stone-100 rounded-xl p-4 space-y-3">
          {form.membership_application_status === "pending" ? (
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-primary font-semibold mb-1">Pengajuan Sedang Direview</p>
              <p className="text-sm text-stone-600">
                Pengajuan menjadi <span className="font-semibold capitalize">{formatRole(form.membership_requested_role)}</span> sedang menunggu approval admin.
              </p>
            </div>
          ) : ["member", "reseller", "agen"].includes(form.role) ? (
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-primary font-semibold mb-1">Membership Aktif</p>
              <p className="text-sm text-stone-600">Akun kamu sudah aktif sebagai <span className="font-semibold">{formatRole(form.role)}</span>.</p>
            </div>
          ) : (
            <>
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-primary font-semibold mb-1">Upgrade Membership</p>
                <p className="text-sm text-stone-600">Ajukan perubahan membership menjadi Member, Reseller, atau Agen.</p>
              </div>
              {!membershipOpen ? (
                <Button type="button" onClick={() => setMembershipOpen(true)} className="w-full gap-2">
                  <ArrowUpCircle className="w-4 h-4" /> Upgrade Membership
                </Button>
              ) : (
                <>
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.18em] text-stone-500 font-semibold mb-1">
                      Nomor WhatsApp
                    </label>
                    <Input
                      value={form.phone}
                      onChange={(e) => updateField("phone", e.target.value)}
                      placeholder="Contoh: 081234567890"
                      className="bg-white"
                      data-testid="profile-membership-phone-input"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { value: "member", label: "Member" },
                      { value: "reseller", label: "Reseller" },
                      { value: "agen", label: "Agen" },
                    ].map((item) => (
                      <button
                        key={item.value}
                        type="button"
                        onClick={() => setMembershipRole(item.value)}
                        className={`py-2 text-xs border rounded-lg transition-colors ${membershipRole === item.value ? "border-primary bg-primary/10 text-primary" : "border-stone-200 bg-white text-stone-600"}`}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={() => setMembershipOpen(false)} className="flex-1">
                      Batal
                    </Button>
                    <Button type="button" onClick={handleApplyMembership} disabled={applyingMembership} className="flex-1 gap-2">
                      <ArrowUpCircle className="w-4 h-4" /> {applyingMembership ? "Mengirim..." : "Ajukan"}
                    </Button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">
        <div className="xl:col-span-2 space-y-6">
          <form onSubmit={handleSaveProfile} className="bg-white border border-stone-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="p-5 border-b border-stone-100 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center"><User className="w-5 h-5 text-stone-600" /></div>
              <div>
                <h2 className="font-serif text-xl text-stone-900">Data Akun</h2>
                <p className="text-xs text-stone-500">Email bersifat readonly untuk menjaga riwayat transaksi.</p>
              </div>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Nama</label>
                <Input value={form.name} onChange={(e) => updateField("name", e.target.value)} disabled={!isAdmin} className="mt-1 disabled:bg-stone-50 disabled:text-stone-600" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Email</label>
                <Input value={form.email} disabled className="mt-1 bg-stone-50 text-stone-500" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Nomor HP / WhatsApp</label>
                <Input value={form.phone} onChange={(e) => updateField("phone", e.target.value)} disabled={!isAdmin} className="mt-1 disabled:bg-stone-50 disabled:text-stone-600" placeholder="08xxxxxxxxxx" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Role</label>
                <Input value={form.role} disabled className="mt-1 bg-stone-50 uppercase text-stone-500" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Alamat Default</label>
                <Textarea value={form.address} onChange={(e) => updateField("address", e.target.value)} disabled={!isAdmin} className="mt-1 min-h-[95px] disabled:bg-stone-50 disabled:text-stone-600" placeholder="Alamat default untuk penerima pengiriman" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Kode Pos</label>
                <Input value={form.postal_code} onChange={(e) => updateField("postal_code", e.target.value)} disabled={!isAdmin} className="mt-1 disabled:bg-stone-50 disabled:text-stone-600" />
              </div>
            </div>

            <div className="p-5 border-t border-stone-100">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-full bg-cyan-50 flex items-center justify-center"><Truck className="w-4 h-4 text-cyan-700" /></div>
                <div>
                  <h3 className="font-serif text-lg text-stone-900">Data Pengirim Default</h3>
                  <p className="text-xs text-stone-500">Dipakai sebagai data pengirim default saat mengatur pengiriman.</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Nama Pengirim</label>
                  <Input value={form.default_sender_name} onChange={(e) => updateField("default_sender_name", e.target.value)} className="mt-1" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Telepon Pengirim</label>
                  <Input value={form.default_sender_phone} onChange={(e) => updateField("default_sender_phone", e.target.value)} className="mt-1" />
                </div>
              </div>
            </div>

            <div className="p-5 border-t border-stone-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              {!isAdmin && (
                <p className="text-xs text-stone-500">
                  Nama, kontak, dan alamat utama hanya bisa diubah oleh admin. Data pengirim default tetap bisa kamu ubah.
                </p>
              )}
              <Button type="submit" disabled={saving} className="gap-2 sm:ml-auto">
                <Save className="w-4 h-4" /> {saving ? "Menyimpan..." : "Simpan Data Pengirim"}
              </Button>
            </div>
          </form>

        </div>

        <div className="space-y-6 xl:sticky xl:top-24">
          <form onSubmit={handleSavePassword} className="bg-white border border-stone-200 rounded-2xl shadow-sm p-5 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-amber-50 flex items-center justify-center"><KeyRound className="w-4 h-4 text-amber-700" /></div>
                <div>
                  <h2 className="font-serif text-lg text-stone-900">Keamanan</h2>
                  <p className="text-xs text-stone-500">Ganti password akun.</p>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Password Lama</label>
                <Input type="password" value={passwordForm.current_password} onChange={(e) => setPasswordForm((p) => ({ ...p, current_password: e.target.value }))} className="mt-1" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Password Baru</label>
                <Input type="password" value={passwordForm.new_password} onChange={(e) => setPasswordForm((p) => ({ ...p, new_password: e.target.value }))} className="mt-1" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Konfirmasi Password</label>
                <Input type="password" value={passwordForm.confirm_password} onChange={(e) => setPasswordForm((p) => ({ ...p, confirm_password: e.target.value }))} className="mt-1" />
              </div>
              <Button type="submit" disabled={savingPassword} variant="outline" className="w-full gap-2">
                <KeyRound className="w-4 h-4" /> {savingPassword ? "Menyimpan..." : "Ganti Password"}
              </Button>
          </form>
        </div>
      </div>

      <section className="bg-white border border-stone-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="p-5 sm:p-6 border-b border-stone-100 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center"><MapPin className="w-5 h-5 text-emerald-700" /></div>
            <div>
              <h2 className="font-serif text-xl text-stone-900">Buku Alamat</h2>
              <p className="text-xs text-stone-500">Simpan alamat penerima dan data dropship. Order menyimpan snapshot alamat saat checkout.</p>
            </div>
          </div>
          <div className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 border border-emerald-100 self-start lg:self-auto">
            {addresses.length} alamat tersimpan
          </div>
        </div>
        <div className="p-5 sm:p-6 grid grid-cols-1 xl:grid-cols-12 gap-6 items-start">
          <div className="xl:col-span-5 space-y-3 max-h-none xl:max-h-[760px] xl:overflow-y-auto xl:pr-2">
            {addresses.length === 0 ? (
              <div className="rounded-xl border border-dashed border-stone-200 p-6 text-sm text-stone-500 bg-stone-50/60">Belum ada alamat tersimpan.</div>
            ) : addresses.map((address) => (
              <div key={address.id} className="rounded-xl border border-stone-200 bg-white p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-semibold text-stone-800 break-words">{address.label || "Alamat"}</p>
                      {address.is_default && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-100">Default</span>}
                      {address.sender_type === "dropship" && <span className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-50 text-cyan-700 border border-cyan-100">Dropship</span>}
                    </div>
                    <p className="text-sm text-stone-700 mt-1">{address.recipient_name} · {address.recipient_phone}</p>
                    <p className="text-xs text-stone-500 mt-2 whitespace-pre-line leading-relaxed break-words">{address.address}</p>
                    {address.postal_code && <p className="text-xs text-stone-500 mt-2">Kode Pos: <span className="font-medium text-stone-700">{address.postal_code}</span></p>}
                    {address.sender_type === "dropship" && <p className="text-xs text-cyan-700 mt-2">Pengirim: {address.sender_name || "-"} · {address.sender_phone || "-"}</p>}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 pt-1">
                  {!address.is_default && <Button type="button" size="sm" variant="outline" onClick={() => setDefaultAddress(address.id)} className="gap-1"><Star className="w-3 h-3" /> Default</Button>}
                  <Button type="button" size="sm" variant="outline" onClick={() => editAddress(address)} className="gap-1"><Pencil className="w-3 h-3" /> Edit</Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => deleteAddress(address.id)} className="gap-1 text-red-600 hover:text-red-700"><Trash2 className="w-3 h-3" /> Arsip</Button>
                </div>
              </div>
            ))}
          </div>

          <form onSubmit={handleSaveAddress} className="xl:col-span-7 space-y-4 rounded-2xl border border-stone-100 bg-stone-50/60 p-4 sm:p-5">
            <div className="flex items-center justify-between gap-2">
              <div>
                <h3 className="font-serif text-lg text-stone-900">{editingAddressId ? "Edit Alamat" : "Tambah Alamat"}</h3>
                <p className="text-xs text-stone-500 mt-1">Area isian dibuat lebih lebar agar alamat lengkap dan data dropship lebih nyaman diisi.</p>
              </div>
              {editingAddressId && <button type="button" onClick={resetAddressForm} className="text-xs text-stone-500 underline shrink-0">Batal edit</button>}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <Input className="lg:col-span-2 bg-white" placeholder="Label alamat, contoh: Rumah / Customer A" value={addressForm.label} onChange={(e) => setAddressForm((p) => ({ ...p, label: e.target.value }))} />
              <Input className="bg-white" placeholder="Nama penerima" value={addressForm.recipient_name} onChange={(e) => setAddressForm((p) => ({ ...p, recipient_name: e.target.value }))} />
              <Input className="bg-white" placeholder="Nomor HP penerima" value={addressForm.recipient_phone} onChange={(e) => setAddressForm((p) => ({ ...p, recipient_phone: e.target.value }))} />
              <Textarea placeholder="Alamat lengkap penerima" className="lg:col-span-2 min-h-[150px] bg-white" value={addressForm.address} onChange={(e) => setAddressForm((p) => ({ ...p, address: e.target.value }))} />
              <Input className="bg-white" placeholder="Kode pos" value={addressForm.postal_code} onChange={(e) => setAddressForm((p) => ({ ...p, postal_code: e.target.value }))} />
              <label className="flex items-center gap-2 text-sm text-stone-700 rounded-md border border-stone-200 bg-white px-3 py-2">
                <input type="checkbox" checked={addressForm.is_default} onChange={(e) => setAddressForm((p) => ({ ...p, is_default: e.target.checked }))} /> Jadikan alamat default
              </label>
            </div>
            <div className="pt-4 border-t border-stone-200 space-y-3">
              <label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Tipe Pengirim</label>
              <select value={addressForm.sender_type} onChange={(e) => setAddressForm((p) => ({ ...p, sender_type: e.target.value }))} className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm">
                <option value="self">Pengirim sendiri/default</option>
                <option value="dropship">Dropship</option>
              </select>
              {addressForm.sender_type === "dropship" && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  <Input className="bg-white" placeholder="Nama pengirim dropship" value={addressForm.sender_name} onChange={(e) => setAddressForm((p) => ({ ...p, sender_name: e.target.value }))} />
                  <Input className="bg-white" placeholder="HP pengirim dropship" value={addressForm.sender_phone} onChange={(e) => setAddressForm((p) => ({ ...p, sender_phone: e.target.value }))} />
                  <Textarea placeholder="Alamat pengirim dropship (opsional)" className="lg:col-span-2 min-h-[110px] bg-white" value={addressForm.sender_address} onChange={(e) => setAddressForm((p) => ({ ...p, sender_address: e.target.value }))} />
                </div>
              )}
            </div>
            <Button type="submit" disabled={savingAddress} className="w-full gap-2"><Plus className="w-4 h-4" /> {savingAddress ? "Menyimpan..." : (editingAddressId ? "Simpan Perubahan" : "Tambah Alamat")}</Button>
          </form>
        </div>
      </section>
      </main>
      <Footer />
    </div>
  );
}
